import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { FileText, ExternalLink } from "lucide-react";
import { useAdminT } from "../../../hooks/useAdminT";
import { RemoteImage } from "../../../core/components/RemoteImage";

export default function BlogPage() {
    const { t } = useAdminT();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [deleteTarget, setDeleteTarget] = useState<any>(null);

    // listAdmin returns FORMATTED: { id, slug, coverImage, title, excerpt, author, category, published }
    const { data: posts, isLoading } = useQuery({
        queryKey: ["blog"],
        queryFn: () => api.get("/api/admin/blog").then(res => res.data.data),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/blog/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["blog"] });
            setDeleteTarget(null);
        },
    });

    return (
        <div className="p-8">
            <PageHeader
                title={t("blog.title")}
                description={t("blog.subtitle")}
                actions={[
                    {
                        label: t("blog.add"),
                        icon: FileText,
                        onClick: () => navigate("/admin/blog/new"),
                    },
                    {
                        label: "View Blog",
                        icon: ExternalLink,
                        onClick: () => window.open("/blog", "_blank"),
                    },
                ]}
            />

            <AdminTable
                data={posts}
                isLoading={isLoading}
                columns={[
                    {
                        header: t("common.image"),
                        cell: (p: any) =>
                            p.coverImage ? (
                                <RemoteImage src={p.coverImage} className="w-16 h-10 object-cover rounded" alt={p.title} />
                            ) : (
                                <div className="w-16 h-10 bg-slate-100 rounded" />
                            ),
                        enableSorting: false,
                    },
                    { header: t("common.title"), cell: (p: any) => p.title || "-" },
                    { header: t("blog.fields.author"), cell: (p: any) => p.author?.name || "-" },
                    { header: t("nav.categories"), cell: (p: any) => p.category?.name || "-" },
                    { header: t("common.published"), cell: (p: any) => <StatusBadge status={p.published} /> },
                ]}
                onEdit={(p: any) => navigate(`/admin/blog/${p.id}/edit`)}
                onDelete={(p: any) => setDeleteTarget(p)}
            />

            <ConfirmDialog
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                title={t("blog.delete")}
                message={`${t("common.confirmProceed")} "${deleteTarget?.title || t("blog.title")}"?`}
                confirmText={t("common.delete")}
                isLoading={deleteMutation.isPending}
            />
        </div>
    );
}
